import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Package, 
  FolderTree, 
  Truck, 
  Tags, 
  ChevronRight,
  DollarSign,
  Clock,
  Layers,
  BarChart3,
  RefreshCw,
  Link as LinkIcon,
  Unlink,
  Box,
  ExternalLink,
  RefreshCcw,
  Plus,
  Eye,
  Edit,
  Upload,
  Search,
  X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import VendorSyncManager from '../../components/admin/VendorSyncManager';
import ProductFormModal from '../../components/admin/ProductFormModal';
import ProductDetailModal from '../../components/admin/ProductDetailModal';
import BulkProductImport from '../../components/admin/BulkProductImport';
import VendorSourceFormModal from '../../components/admin/VendorSourceFormModal';
import VendorItemFormModal from '../../components/admin/VendorItemFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Product type labels and colors
const PRODUCT_TYPE_CONFIG = {
  AWD: { label: 'Awards', color: 'bg-amber-500', icon: '🏆' },
  LGF: { label: 'Large Format', color: 'bg-blue-500', icon: '🖼️' },
  SMF: { label: 'Small Format', color: 'bg-green-500', icon: '📄' },
  PRO: { label: 'Promotional', color: 'bg-purple-500', icon: '👕' }
};

// Pricing type badges
const PRICING_TYPE_BADGE = {
  matrix: { label: 'Matrix', variant: 'default' },
  per_sqft: { label: 'Per Sq Ft', variant: 'secondary' },
  option_matrix: { label: 'Options', variant: 'outline' },
  fixed: { label: 'Fixed', variant: 'secondary' }
};

// Vendor item status colors
const VENDOR_ITEM_STATUS = {
  active: { label: 'Active', color: 'bg-green-500 text-white' },
  inactive: { label: 'Inactive', color: 'bg-gray-400 text-white' },
  discontinued: { label: 'Discontinued', color: 'bg-red-500 text-white' },
  pending: { label: 'Pending', color: 'bg-yellow-500 text-white' },
  new: { label: 'New', color: 'bg-blue-500 text-white' }
};

export default function AdminCatalogOverview() {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [vendorSources, setVendorSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendorItems, setVendorItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVendorItem, setSelectedVendorItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Product modal states (Phase 6)
  const [showProductForm, setShowProductForm] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [viewProductId, setViewProductId] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  
  // Vendor modal states
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editVendorId, setEditVendorId] = useState(null);
  
  // Vendor Item modal states
  const [showVendorItemForm, setShowVendorItemForm] = useState(false);
  const [editVendorItemId, setEditVendorItemId] = useState(null);
  
  // Search state for Products tab
  const [productSearch, setProductSearch] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, catsRes, vendorsRes, tagsRes, prodsRes, vendorItemsRes] = await Promise.all([
        fetch(`${API_URL}/api/catalog/stats`),
        fetch(`${API_URL}/api/catalog/categories`),
        fetch(`${API_URL}/api/catalog/vendor-sources`),
        fetch(`${API_URL}/api/catalog/tags`),
        fetch(`${API_URL}/api/catalog/products?limit=100`),
        fetch(`${API_URL}/api/catalog/vendor-items?limit=100`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (catsRes.ok) {
        const data = await catsRes.json();
        setCategories(data.categories || []);
      }
      if (vendorsRes.ok) {
        const data = await vendorsRes.json();
        setVendorSources(data.vendor_sources || []);
      }
      if (tagsRes.ok) {
        const data = await tagsRes.json();
        setTags(data.tags || []);
      }
      if (prodsRes.ok) {
        const data = await prodsRes.json();
        setProducts(data.products || []);
      }
      if (vendorItemsRes.ok) {
        const data = await vendorItemsRes.json();
        setVendorItems(data.vendor_items || []);
      }
    } catch (error) {
      console.error('Error loading catalog data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
     
  }, []);

  const loadProductDetail = async (productId) => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/products/${productId}`);
      if (res.ok) {
        setSelectedProduct(await res.json());
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
  };

  const loadVendorItemDetail = async (itemId) => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/vendor-items/${itemId}`);
      if (res.ok) {
        setSelectedVendorItem(await res.json());
      }
    } catch (error) {
      console.error('Error loading vendor item:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalog-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Catalog Overview</h1>
          <p className="text-gray-500">Phase 1 & 2 - Categories, Vendors, Products, Vendor Items</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_products}</p>
                  <p className="text-sm text-gray-500">Products</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FolderTree className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_categories}</p>
                  <p className="text-sm text-gray-500">Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Truck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_vendor_sources}</p>
                  <p className="text-sm text-gray-500">Vendors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Tags className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_tags}</p>
                  <p className="text-sm text-gray-500">Tags</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="vendor-items-stat">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Box className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_vendor_items}</p>
                  <p className="text-sm text-gray-500">Vendor Items</p>
                  <p className="text-xs text-gray-400">
                    {stats.linked_vendor_items} linked
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="vendor-items" data-testid="vendor-items-tab">Vendor Items</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="sync" data-testid="sync-tab">
            <RefreshCcw className="w-4 h-4 mr-1" />
            Sync
          </TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Products by Type */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Products by Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(PRODUCT_TYPE_CONFIG).map(([code, config]) => (
                  <div 
                    key={code}
                    className="p-4 rounded-lg border bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{config.icon}</span>
                      <span className="font-medium">{config.label}</span>
                    </div>
                    <p className="text-3xl font-bold">
                      {stats?.products_by_type?.[code] || 0}
                    </p>
                    <p className="text-sm text-gray-500">
                      {categories.filter(c => c.product_type === code).length} categories
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Products by Pricing Type */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Products by Pricing Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(stats?.products_by_pricing || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center gap-2 p-3 border rounded-lg">
                    <Badge variant={PRICING_TYPE_BADGE[type]?.variant || 'default'}>
                      {PRICING_TYPE_BADGE[type]?.label || type}
                    </Badge>
                    <span className="font-bold">{count}</span>
                    <span className="text-gray-500">products</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <CardTitle>Categories by Product Type</CardTitle>
              <CardDescription>
                {categories.length} categories across {Object.keys(PRODUCT_TYPE_CONFIG).length} product types
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(PRODUCT_TYPE_CONFIG).map(([code, config]) => {
                  const typeCats = categories.filter(c => c.product_type === code);
                  if (typeCats.length === 0) return null;
                  
                  return (
                    <div key={code}>
                      <h3 className="flex items-center gap-2 font-semibold mb-3">
                        <span className={`w-3 h-3 rounded-full ${config.color}`}></span>
                        {config.icon} {config.label}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {typeCats.map(cat => (
                          <div 
                            key={cat.id}
                            className="p-3 border rounded-lg hover:border-gray-400 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">{cat.name}</p>
                                <p className="text-sm text-gray-500">Code: {cat.code}</p>
                              </div>
                              <Badge variant="outline">{cat.product_count}</Badge>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {cat.pricing_type}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {cat.default_markup_percent}% markup
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Vendor Sources</CardTitle>
                  <CardDescription>
                    {vendorSources.length} configured vendor sources
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => { setEditVendorId(null); setShowVendorForm(true); }}
                  data-testid="add-vendor-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vendor
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vendorSources.map(vendor => (
                  <div 
                    key={vendor.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => { setEditVendorId(vendor.id); setShowVendorForm(true); }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold">{vendor.name}</h4>
                        <p className="text-sm text-gray-500">Code: {vendor.code}</p>
                      </div>
                      <Badge 
                        variant={vendor.vendor_type === 'white_label' ? 'secondary' : 'default'}
                      >
                        {vendor.vendor_type}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      {vendor.vendor_type !== 'white_label' && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span>Default Markup: {vendor.default_markup_percent}%</span>
                        </div>
                      )}
                      
                      {vendor.website && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">🌐</span>
                          <a 
                            href={vendor.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {vendor.website}
                          </a>
                        </div>
                      )}
                      
                      {vendor.contact?.email && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">📧</span>
                          <span>{vendor.contact.email}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-gray-400" />
                        <span>
                          Sync: {vendor.sync_config?.method || 'csv'} / {vendor.sync_config?.frequency || 'manual'}
                        </span>
                      </div>
                    </div>
                    
                    {vendor.notes && (
                      <p className="mt-3 text-sm text-gray-600 italic border-t pt-2">
                        {vendor.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Product List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Catalog Products</CardTitle>
                    <CardDescription>
                      {products.length} products in catalog
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => { setEditProductId(null); setShowProductForm(true); }}
                      data-testid="create-product-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setShowBulkImport(true)}
                      data-testid="bulk-import-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Bulk Import
                    </Button>
                  </div>
                </div>
                {/* Search Input */}
                <div className="mt-4 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by SKU..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-9 pr-9"
                    data-testid="product-search-input"
                  />
                  {productSearch && (
                    <button
                      onClick={() => setProductSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="clear-search-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {products
                    .filter(product => 
                      productSearch === '' || 
                      product.sku?.toLowerCase().includes(productSearch.toLowerCase())
                    )
                    .map(product => (
                    <div 
                      key={product.id}
                      className={`p-3 border rounded-lg transition-all ${
                        selectedProduct?.id === product.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {product.image_url && (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 cursor-pointer" onClick={() => loadProductDetail(product.id)}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-500">{product.sku}</span>
                            <ChevronRight className="w-4 h-4 text-gray-300" />
                          </div>
                          <p className="font-medium">{product.name}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {PRODUCT_TYPE_CONFIG[product.product_type]?.label || product.product_type}
                            </Badge>
                            <Badge 
                              variant={PRICING_TYPE_BADGE[product.pricing_type]?.variant || 'default'}
                              className="text-xs"
                            >
                              {PRICING_TYPE_BADGE[product.pricing_type]?.label || product.pricing_type}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">${product.retail_price?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">
                            {product.variants?.length || 0} variants
                          </p>
                        </div>
                        {/* Action buttons */}
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setViewProductId(product.id)}
                            title="View Details"
                            data-testid={`view-product-${product.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setEditProductId(product.id); setShowProductForm(true); }}
                            title="Edit Product"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* No results message */}
                  {productSearch && products.filter(p => p.sku?.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                    <div className="text-center py-8 text-gray-500" data-testid="no-products-found">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No products found matching &quot;{productSearch}&quot;</p>
                      <p className="text-sm mt-1">Try a different SKU</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Product Detail */}
            <Card>
              <CardHeader>
                <CardTitle>Product Detail</CardTitle>
                <CardDescription>
                  {selectedProduct ? selectedProduct.sku : 'Select a product'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedProduct ? (
                  <div className="space-y-4">
                    {selectedProduct.image_url && (
                      <img 
                        src={selectedProduct.image_url} 
                        alt={selectedProduct.name}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    )}
                    
                    <div>
                      <h4 className="font-semibold">{selectedProduct.name}</h4>
                      <p className="text-sm text-gray-600">{selectedProduct.description}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Lead time: {selectedProduct.lead_time_days} days</span>
                    </div>

                    {/* Tags */}
                    {selectedProduct.tag_details?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-1">Tags:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedProduct.tag_details.map(tag => (
                            <Badge 
                              key={tag.slug} 
                              variant="outline"
                              style={{ borderColor: tag.color, color: tag.color }}
                            >
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Variants (Matrix Pricing) */}
                    {selectedProduct.variants && (
                      <div>
                        <p className="text-sm font-medium mb-2">Variants & Pricing:</p>
                        <div className="space-y-2">
                          {selectedProduct.variants.map((v, i) => (
                            <div key={i} className="p-2 bg-gray-50 rounded text-sm">
                              <div className="flex justify-between font-medium">
                                <span>{v.name}</span>
                                <span>${v.retail?.toFixed(2)}</span>
                              </div>
                              {v.dimensions && (
                                <p className="text-gray-500 text-xs">{v.dimensions}</p>
                              )}
                              {v.qty_pricing && (
                                <div className="mt-1 text-xs text-gray-600">
                                  {v.qty_pricing.map((tier, ti) => (
                                    <span key={ti} className="mr-2">
                                      {tier.min_qty}+: ${tier.retail?.toFixed(2)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Per Sqft Pricing */}
                    {selectedProduct.sqft_pricing && (
                      <div>
                        <p className="text-sm font-medium mb-2">Sq Ft Pricing:</p>
                        <div className="p-2 bg-gray-50 rounded text-sm">
                          <p>Base Rate: ${selectedProduct.sqft_pricing.base_rate}/sqft</p>
                          <p>Minimum: ${selectedProduct.sqft_pricing.min_charge}</p>
                        </div>
                        {selectedProduct.material_options && (
                          <div className="mt-2 space-y-1">
                            {selectedProduct.material_options.map((m, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span>{m.name}</span>
                                <span>${m.rate}/sqft (min ${m.min_charge})</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Option Matrix Pricing */}
                    {selectedProduct.options && (
                      <div>
                        <p className="text-sm font-medium mb-2">Options:</p>
                        <div className="space-y-2">
                          {Object.entries(selectedProduct.options).map(([key, config]) => (
                            <div key={key} className="text-sm">
                              <span className="font-medium capitalize">{key}: </span>
                              <span className="text-gray-600">
                                {config.values?.join(', ')}
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {selectedProduct.price_table?.length} price combinations
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Click a product to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vendor Items Tab (Phase 2) */}
        <TabsContent value="vendor-items" data-testid="vendor-items-content">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Vendor Items List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Box className="w-5 h-5" />
                      Vendor Catalog Items
                    </CardTitle>
                    <CardDescription>
                      {vendorItems.length} raw vendor items • {stats?.linked_vendor_items || 0} linked to products
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => { setEditVendorItemId(null); setShowVendorItemForm(true); }}
                    data-testid="add-vendor-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vendor Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {vendorItems.map(item => (
                    <div 
                      key={item.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedVendorItem?.id === item.id 
                          ? 'border-cyan-500 bg-cyan-50' 
                          : 'hover:border-gray-400'
                      }`}
                      onClick={() => loadVendorItemDetail(item.id)}
                      data-testid={`vendor-item-${item.vendor_sku}`}
                    >
                      <div className="flex items-center gap-3">
                        {item.image_urls?.[0] && (
                          <img 
                            src={item.image_urls[0]} 
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm text-gray-500">{item.vendor_sku}</span>
                            {item.is_linked ? (
                              <Badge variant="default" className="gap-1 text-xs">
                                <LinkIcon className="w-3 h-3" /> Linked
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="gap-1 text-xs text-gray-500">
                                <Unlink className="w-3 h-3" /> Unlinked
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium truncate">{item.name}</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            <Badge variant="secondary" className="text-xs">
                              {item.vendor_name || item.vendor_code}
                            </Badge>
                            {item.category_code && (
                              <Badge variant="outline" className="text-xs">
                                {item.category_code}
                              </Badge>
                            )}
                            <Badge className={`text-xs ${VENDOR_ITEM_STATUS[item.status]?.color || 'bg-gray-400'}`}>
                              {VENDOR_ITEM_STATUS[item.status]?.label || item.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">${item.cost_price?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">
                            {item.variant_count || 0} variants
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {vendorItems.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Box className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No vendor items imported yet</p>
                      <p className="text-sm">Import vendor catalogs to populate this list</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Vendor Item Detail */}
            <Card>
              <CardHeader>
                <CardTitle>Vendor Item Detail</CardTitle>
                <CardDescription>
                  {selectedVendorItem ? selectedVendorItem.vendor_sku : 'Select an item'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedVendorItem ? (
                  <div className="space-y-4">
                    {selectedVendorItem.image_urls?.[0] && (
                      <img 
                        src={selectedVendorItem.image_urls[0]} 
                        alt={selectedVendorItem.name}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    )}
                    
                    <div>
                      <h4 className="font-semibold">{selectedVendorItem.name}</h4>
                      {selectedVendorItem.brand && (
                        <p className="text-sm text-gray-500">Brand: {selectedVendorItem.brand}</p>
                      )}
                      <p className="text-sm text-gray-600 mt-2">{selectedVendorItem.description}</p>
                    </div>

                    {/* Vendor Info */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Vendor Information</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Vendor:</span>
                          <span>{selectedVendorItem.vendor_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Type:</span>
                          <Badge variant="outline">{selectedVendorItem.vendor_type}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Vendor SKU:</span>
                          <span className="font-mono">{selectedVendorItem.vendor_sku}</span>
                        </div>
                        {selectedVendorItem.vendor_product_id && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Vendor ID:</span>
                            <span className="font-mono">{selectedVendorItem.vendor_product_id}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Pricing</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Cost Price:</span>
                          <span className="font-bold text-green-600">${selectedVendorItem.cost_price?.toFixed(2)}</span>
                        </div>
                        {selectedVendorItem.msrp && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">MSRP:</span>
                            <span>${selectedVendorItem.msrp?.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Link Status */}
                    <div className="p-3 rounded-lg border">
                      <p className="text-sm font-medium mb-2">Product Link</p>
                      {selectedVendorItem.linked_product_id ? (
                        <div className="flex items-center gap-2">
                          <LinkIcon className="w-4 h-4 text-green-600" />
                          <span className="text-sm">
                            Linked to: {selectedVendorItem.linked_product_sku || selectedVendorItem.linked_product_id}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Unlink className="w-4 h-4" />
                          <span className="text-sm">Not linked to any product</span>
                        </div>
                      )}
                    </div>

                    {/* Variants */}
                    {selectedVendorItem.variants?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Variants ({selectedVendorItem.variants.length})</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {selectedVendorItem.variants.map((v, i) => (
                            <div key={i} className="p-2 bg-gray-50 rounded text-sm">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{v.name}</span>
                                <span className="text-green-600">${v.cost_price?.toFixed(2)}</span>
                              </div>
                              <div className="flex gap-2 mt-1 flex-wrap text-xs text-gray-500">
                                {v.size && <span>Size: {v.size}</span>}
                                {v.color && <span>Color: {v.color}</span>}
                                {v.in_stock !== undefined && (
                                  <span className={v.in_stock ? 'text-green-600' : 'text-red-600'}>
                                    {v.in_stock ? '✓ In Stock' : '✗ Out of Stock'}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Raw Vendor Data */}
                    {selectedVendorItem.vendor_data && Object.keys(selectedVendorItem.vendor_data).length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Additional Vendor Data</p>
                        <div className="p-2 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-32">
                          <pre>{JSON.stringify(selectedVendorItem.vendor_data, null, 2)}</pre>
                        </div>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="text-xs text-gray-400 pt-2 border-t">
                      <p>Imported: {new Date(selectedVendorItem.imported_at).toLocaleString()}</p>
                      {selectedVendorItem.synced_at && (
                        <p>Last Sync: {new Date(selectedVendorItem.synced_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Box className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Click a vendor item to view details</p>
                    <p className="text-sm mt-2 text-gray-400">
                      Vendor items are raw catalog data from your suppliers
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sync Tab (Phase 4) */}
        <TabsContent value="sync" data-testid="sync-tab-content">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCcw className="w-5 h-5" />
                  Vendor Catalog Sync
                </CardTitle>
                <CardDescription>
                  Sync vendor catalogs to update prices, availability, and add new items
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Select a vendor below to manage their sync settings and history.
                </p>
                
                {/* Vendor Sync Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {vendorSources.map(vendor => (
                    <div 
                      key={vendor.id}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Truck className="w-5 h-5 text-gray-500" />
                          <div>
                            <p className="font-medium">{vendor.name}</p>
                            <p className="text-xs text-gray-500">{vendor.code} • {vendor.vendor_type}</p>
                          </div>
                        </div>
                        <Badge variant={vendor.is_active ? 'default' : 'secondary'}>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="p-3">
                        <VendorSyncManager
                          vendorId={vendor.id}
                          vendorName={vendor.name}
                          vendorCode={vendor.code}
                          onSyncComplete={loadData}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {vendorSources.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No vendors configured</p>
                    <p className="text-sm">Add vendor sources first to enable sync</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags">
          <Card>
            <CardHeader>
              <CardTitle>Theme Tags</CardTitle>
              <CardDescription>
                Cross-cutting tags for product categorization by use-case
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {tags.map(tag => (
                  <div 
                    key={tag.id}
                    className="p-3 border rounded-lg"
                    style={{ borderColor: tag.color }}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      ></div>
                      <span className="font-medium">{tag.name}</span>
                      <Badge variant="outline">{tag.product_count}</Badge>
                    </div>
                    {tag.description && (
                      <p className="text-sm text-gray-500 mt-1">{tag.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Form Modal (Create/Edit) - Phase 6 */}
      <ProductFormModal
        open={showProductForm}
        onOpenChange={(open) => {
          setShowProductForm(open);
          if (!open) setEditProductId(null);
        }}
        productId={editProductId}
        onSave={() => {
          loadData();
          setShowProductForm(false);
          setEditProductId(null);
        }}
      />

      {/* Product Detail Modal (View) */}
      <ProductDetailModal
        open={!!viewProductId}
        onOpenChange={(open) => {
          if (!open) setViewProductId(null);
        }}
        productId={viewProductId}
        onUpdate={loadData}
      />

      {/* Bulk Product Import Modal */}
      <BulkProductImport
        open={showBulkImport}
        onOpenChange={setShowBulkImport}
        categories={categories}
        onImportComplete={loadData}
      />

      {/* Vendor Source Form Modal */}
      <VendorSourceFormModal
        open={showVendorForm}
        onOpenChange={(open) => {
          setShowVendorForm(open);
          if (!open) setEditVendorId(null);
        }}
        vendorId={editVendorId}
        onSave={() => {
          loadData();
          setShowVendorForm(false);
          setEditVendorId(null);
        }}
      />

      {/* Vendor Item Form Modal */}
      <VendorItemFormModal
        open={showVendorItemForm}
        onOpenChange={(open) => {
          setShowVendorItemForm(open);
          if (!open) setEditVendorItemId(null);
        }}
        itemId={editVendorItemId}
        vendorSources={vendorSources}
        categories={categories}
        onSave={() => {
          loadData();
          setShowVendorItemForm(false);
          setEditVendorItemId(null);
        }}
      />
    </div>
  );
}
